import colors from '../utils/colors';
import { darkenColor, increaseColorBrightness } from '../utils/colorUtils';
import { currentTheme } from '../utils/theme/appTheme';
import { rawColors } from '../utils/theme/rawColors';
import { mixHex, SoftBackground } from '../utils/theme/softBackground';

export type Theme = 'dark' | 'light';

export const lightTheme = {
  name: 'light',
  primary: colors.primaryPurple,
  primaryLight: colors.primaryLight,
  primaryDark: colors.primaryDark,
  background: colors.white,
  backgroundDark: colors.whitesmoke,
  backgroundLight: colors.backgroundLight,
  borderColor: colors.lightgray,
  // A text box being typed in or pointed at; the border grey on white and Slate, a deeper shade of a soft background.
  inputActiveColor: colors.lightgray,
  captures: colors.captures,
  textColor: colors.textDefault,
  textStrongColor: colors.darkGray,
  disabledTextColor: colors.gray,
  activitiesColor: colors.whitesmoke,
  courseColorFunction: increaseColorBrightness,
  commitmentBorderFunction: color => increaseColorBrightness(color, 70),
  commitmentColorFunction: color => increaseColorBrightness(color, 90),
  eventColorFunction: color => color,
  tooltipTextColor: colors.white,
  plannedTagColor: color =>
    color === colors.positive
      ? colors.lightPositive
      : color === colors.negative
      ? colors.lightestNegative
      : colors.primaryLight,
  plannedProgressColor: color => colors.lightNegative,
  // How much of a task is done, on its card: the course colour this strong, and a line where it ends (Petr, 2026-10-08:
  // at 5% it was hard to see; 2026-10-09: the line a tad softer, from 0.5).
  taskProgressOpacity: 0.12,
  taskProgressEdgeOpacity: 0.4,
  awakeEventColor: colors.whitesmoke,
  studyTimeColor: increaseColorBrightness(colors.primaryPurple, 97),
  passedStudyColor: colors.whitesmoke,
  planOverlayColor: darkenColor(colors.primaryLight, 8),
  calendarGrayColor: '#F2F3F5',
  // The calendar's hour lines and the lines between days in the pill row.
  calendarDayLine: '#F7F8FA',
  // Auto-plan's and the Task Estimator's surface: a hair off white, so their white cards stand out from it.
  dialogSurface: '#FAFBFD',
  extraTimeColor: '#EFEFEF',
  extraTimeTextColor: '#BBBBBB',
  sleepTimeBackgroundColor: darkenColor(colors.lightgray, -5),
  boxShadow: 'rgba(63, 78, 97, 0.2)',
  largeBoxShadow: 'rgba(63, 78, 97, 0.3)',
  modalOverlayColor: 'rgba(0, 0, 0, 0.5)',
  displayAuthImage: true,
  tippyThemeName: 'shovel',
  threeDotsTippyTheme: 'three-dots',
  sidebarCollapsedColor: colors.primaryLight,
  sidebarHandleColor: colors.primaryLight,
  sidebarHandleIconColor: colors.primaryPurple,
  verticalLinesThemeName: 'light-theme-vertical-line',
  horizontalLinesThemeName: 'light-theme-horizontal-line',
  onboardingBarBackground: colors.darkGray,
  placeholder: colors.placeholder,
  onboardingBarTextColor: colors.captures,
  onboardingBarBadgesBgColor: '#13181D',
  onboardingBarBadgesColor: colors.darkGray,
  onBoardingStepColor: colors.captures,
  onBoardingStepActiveColor: colors.white,
  hintColor: 'rgba(209, 241, 229, 0.6)',
  negative: colors.negative,
  themeIconCircle: colors.white,
  randomTaskColor: colors.randomTaskColor
};

/**
 * The light theme on the student's soft background (Petr, 2026-10-10). The calendar's awake and passed study blocks were
 * whitesmoke, the panel shade, so they take the background's own panel shade with it.
 */
export const lightThemeOn = (soft: SoftBackground) =>
  soft.key === 'white'
    ? lightTheme
    : {
        ...lightTheme,
        background: soft.background,
        backgroundLight: soft.backgroundLight,
        backgroundDark: soft.backgroundDark,
        activitiesColor: soft.backgroundDark,
        awakeEventColor: soft.backgroundDark,
        passedStudyColor: soft.backgroundDark,
        // Borders and a text box in use: the panel shade a little toward the text's slate, as #E0E6ED is on white
        // (Petr, 2026-10-10: the cool grey stood out on the soft backgrounds).
        borderColor: mixHex(soft.backgroundDark, colors.darkGray, 0.08),
        inputActiveColor: mixHex(soft.backgroundDark, colors.darkGray, 0.08),
        // The calendar's greys, as far from the background as #F2F3F5 and #F7F8FA are from white.
        calendarGrayColor: mixHex(soft.background, colors.darkGray, 0.045),
        calendarDayLine: mixHex(soft.background, colors.darkGray, 0.025),
        dialogSurface: mixHex(soft.background, soft.backgroundLight, 0.5),
        // Sleep time's wash: as far below the panels as #D4DAE1 is below whitesmoke.
        sleepTimeBackgroundColor: mixHex(soft.backgroundDark, colors.darkGray, 0.13),
        // Form boxes' outline: as far below the panels as #D3DCE6 is below whitesmoke.
        inputBorderColor: mixHex(soft.backgroundDark, colors.darkGray, 0.12),
        themeIconCircle: soft.background
      };

export const darkTheme = {
  name: 'dark',
  primary: colors.primaryPurple,
  primaryLight: colors.primaryLight,
  primaryDark: colors.primaryDark,
  background: rawColors.gray2,
  backgroundDark: darkenColor(rawColors.gray2, -15),
  backgroundLight: darkenColor(rawColors.gray2, -15),
  studyTimeColor: darkenColor(rawColors.gray2, -15),
  borderColor: colors.darkGray,
  inputActiveColor: colors.darkGray,
  captures: colors.darkGray,
  textColor: darkenColor(colors.captures, -10),
  textStrongColor: darkenColor(colors.white, -15),
  disabledTextColor: colors.gray,
  activitiesColor: colors.darkGray,
  courseColorFunction: color => increaseColorBrightness(darkenColor(color, -70), 10),
  commitmentBorderFunction: color => darkenColor(color, -45),
  commitmentColorFunction: color => increaseColorBrightness(darkenColor(color, -80), 10),
  eventColorFunction: color => darkenColor(color, -30),
  tooltipTextColor: colors.darkGray,
  plannedTagColor: color => `${color}23`,
  plannedProgressColor: color => `${color}33`,
  taskProgressOpacity: 0.2,
  taskProgressEdgeOpacity: 0.48,
  awakeEventColor: `${currentTheme.primaryDark}2E`,
  passedStudyColor: rawColors.gray2,
  planOverlayColor: `${currentTheme.primary}1A`,
  calendarGrayColor: colors.black,
  extraTimeColor: darkenColor('#EFEFEF', -70),
  extraTimeTextColor: darkenColor('#BBBBBB', -40),
  sleepTimeBackgroundColor: '#292e3c',
  boxShadow: 'rgba(0, 0, 0, 0.4)',
  largeBoxShadow: 'rgba(0, 0, 0, 0.4)',
  modalOverlayColor: 'rgba(0, 0, 0, 0.4)',
  displayAuthImage: false,
  tippyThemeName: 'shovel-dark',
  threeDotsTippyTheme: 'three-dots-dark',
  sidebarCollapsedColor: colors.darkGray,
  sidebarHandleColor: colors.darkGray,
  sidebarHandleIconColor: colors.white,
  verticalLinesThemeName: 'dark-theme-vertical-line',
  horizontalLinesThemeName: 'dark-theme-horizontal-line',
  onboardingBarBackground: colors.backgroundLight,
  placeholder: darkenColor(colors.gray, -20),
  onboardingBarTextColor: colors.darkGray,
  onboardingBarBadgesBgColor: colors.whitesmoke,
  onboardingBarBadgesColor: colors.lightgray,
  onBoardingStepColor: colors.gray,
  onBoardingStepActiveColor: colors.darkGray,
  hintColor: `${colors.positive}23`,
  negative: colors.negative,
  themeIconCircle: darkenColor(colors.white, -15),
  randomTaskColor: colors.randomTaskColor
};

const HEX = /^#[0-9a-f]{6}$/i;

// `background` with `amount` of a course colour in it; a colour that isn't a plain hex goes to `fallback`.
const tintOf = (background: string, amount: number, fallback: (color: string) => string) => (color: string) =>
  HEX.test(color) && HEX.test(background) ? mixHex(background, color, amount) : fallback(color);

/** The dark theme on the student's dark background (Petr, 2026-10-10); Slate is the dark theme as it was. */
export const darkThemeOn = (soft: SoftBackground) =>
  soft.key === 'slate'
    ? darkTheme
    : {
        ...darkTheme,
        background: soft.background,
        backgroundLight: soft.backgroundLight,
        backgroundDark: soft.backgroundDark,
        studyTimeColor: soft.backgroundDark,
        passedStudyColor: soft.background,
        // Slate's borders are a blue-grey; on the others, the card shade a little toward white.
        borderColor: mixHex(soft.background, colors.white, 0.12),
        inputActiveColor: mixHex(soft.background, colors.white, 0.15),
        inputBorderColor: mixHex(soft.background, colors.white, 0.12),
        // The sidebar's fold handle was Slate's blue-grey on every dark background (Petr, 2026-10-11, with Onyx).
        sidebarCollapsedColor: mixHex(soft.background, colors.white, 0.15),
        sidebarHandleColor: mixHex(soft.background, colors.white, 0.15),
        // The "soft" event style (and its preview in Personalization): a step above the cards, in the background's own
        // shade rather than Slate's blue-grey (Petr, 2026-10-11).
        activitiesColor: mixHex(soft.background, colors.white, 0.13),
        // Slate's sleep time is a blue-grey; on the others it is their own deepest shade.
        sleepTimeBackgroundColor: soft.menu ?? soft.backgroundDark,
        // Event fills as the background tinted with the course colour, not the colour darkened to suit Slate: on a
        // lighter dark (Navy) those came out darker than the calendar, like holes (Petr, 2026-10-11).
        commitmentColorFunction: tintOf(soft.background, 0.14, darkTheme.commitmentColorFunction),
        courseColorFunction: tintOf(soft.background, 0.26, darkTheme.courseColorFunction),
        // Past blocks fade toward the calendar itself, not the deepest shade, which ringed them in near-black on Navy.
        passedWashColor: soft.background
      };
